/**
 * Timed-block client helpers.
 *
 * The wall clock on a trial day-4 set belongs to the BACKEND: it stamps
 * `startedAt` and refuses the next answer with
 * `failed-precondition: day4_time_expired` once the limit is past
 * (aswb-backend services/answerService.ts). The client clock only displays it.
 *
 * @module utils/timeLimit
 */

export const TIME_EXPIRED_MARKER = 'day4_time_expired';

/** Typed error the API layer re-throws so the quiz can render the time-up state. */
export class TimeExpiredError extends Error {
  constructor() {
    super(TIME_EXPIRED_MARKER);
    this.name = 'TimeExpiredError';
    Object.setPrototypeOf(this, TimeExpiredError.prototype);
  }
}

/**
 * Deliberately not a type predicate: TimeExpiredError adds no members to Error,
 * so narrowing it out of an `Error` leaves `never` and every later `.message`
 * read stops compiling.
 */
export const isTimeExpiredError = (error: unknown): boolean =>
  error instanceof TimeExpiredError;

/**
 * The raw callable error for an expired timed block. Matched on the marker, not
 * the code: `failed-precondition` is also how a completed session and a
 * malformed request arrive.
 */
export const isRawTimeExpiredError = (error: unknown): boolean =>
  ((error as { message?: string })?.message ?? '').includes(TIME_EXPIRED_MARKER);

/** Seconds as mm:ss, clamped at zero. */
export const formatCountdown = (seconds: number): string => {
  const total = Number.isFinite(seconds) ? Math.max(0, Math.floor(seconds)) : 0;
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
};

/** Seconds at or below which the countdown warns. */
export const COUNTDOWN_WARN_SECONDS = 60;
